import {
  TEntityResponse,
  TEntityListResponse,
  TAreaResBody,
  TAreaSummaryResBody,
  TMulticastGroupSummaryResBody,
  TSubgroupSummaryResBody,
  TCreateAreaReqBody,
  TUpdateAreaReqBody,
} from '@poseidon-city/shared';

import { TAPIClient } from '../client';

type TAreasClient = {
  create(body: TCreateAreaReqBody): Promise<TEntityResponse<'area', TAreaResBody> | undefined>;
  update(id: string, body: TUpdateAreaReqBody): Promise<TEntityResponse<'area', TAreaResBody> | undefined>;
  delete(id: string): Promise<void>;
  get(id: string): Promise<TEntityResponse<'area', TAreaResBody> | undefined>;
  getSuntimes(id: string): Promise<any | undefined>;
  getMulticastGroups(
    id: string
  ): Promise<TEntityListResponse<'multicast-group', TMulticastGroupSummaryResBody> | undefined>;
  getSubGroups(areaId: string): Promise<TEntityListResponse<'subgroup', TSubgroupSummaryResBody> | undefined>;
  list(
    limit: number,
    page: number,
    sort?: string
  ): Promise<TEntityListResponse<'area', TAreaSummaryResBody> | undefined>;
};

function AreasClient(apiClient: TAPIClient): TAreasClient {
  return {
    async create(body: TCreateAreaReqBody): Promise<TEntityResponse<'area', TAreaResBody> | undefined> {
      const data = await apiClient.post<TCreateAreaReqBody, TEntityResponse<'area', TAreaResBody>>(`/areas`, body);

      if (data && data.type !== 'area') {
        throw new Error('Invalid entity type received, expected: area, received: ' + data.type);
      }

      return data;
    },

    async update(id: string, body: TUpdateAreaReqBody): Promise<TEntityResponse<'area', TAreaResBody> | undefined> {
      const data = await apiClient.put<TUpdateAreaReqBody, TEntityResponse<'area', TAreaResBody>>(`/areas/${id}`, body);

      if (data && data.type !== 'area') {
        throw new Error('Invalid entity type received, expected: area, received: ' + data.type);
      }

      return data;
    },

    async delete(id: string): Promise<void> {
      return await apiClient.delete(`/areas/${id}`);
    },

    async get(id: string): Promise<TEntityResponse<'area', TAreaResBody> | undefined> {
      const data = await apiClient.get<TEntityResponse<'area', TAreaResBody>>(`/areas/${id}`);

      if (data && data.type !== 'area') {
        throw new Error('Invalid entity type received, expected: area, received: ' + data.type);
      }

      return data;
    },

    async getSuntimes(id: string): Promise<any | undefined> {
      return apiClient.get<any>(`/areas/${id}/sun-times`);
    },

    async getMulticastGroups(
      id: string
    ): Promise<TEntityListResponse<'multicast-group', TMulticastGroupSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'multicast-group', TMulticastGroupSummaryResBody>>(
        `/areas/${id}/multicast-groups`
      );

      if (data && data.type !== 'multicast-group') {
        throw new Error('Invalid entity type received, expected: multicast-group, received: ' + data.type);
      }

      return data;
    },

    async getSubGroups(areaId: string): Promise<TEntityListResponse<'subgroup', TSubgroupSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'subgroup', TSubgroupSummaryResBody>>(
        `/areas/${areaId}/subgroups`
      );

      if (data && data.type !== 'subgroup') {
        throw new Error('Invalid entity type received, expected: subgroup, received: ' + data.type);
      }

      return data;
    },

    async list(
      limit: number,
      page: number,
      sort?: string
    ): Promise<TEntityListResponse<'area', TAreaSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'area', TAreaSummaryResBody>>(
        `/areas?limit=${limit}&page=${page}${sort}`
      );

      if (data && data.type !== 'area') {
        throw new Error('Invalid entity type received, expected: area, received: ' + data.type);
      }

      return data;
    },
  };
}

export { TAreasClient, AreasClient };
